/**
 *
 * @component 个人中心/我的方案/项目管理/项目协同/项目成员tab
 *
 **/

import {
    Button,
    Table,
    Popover,
    Space,
    Form,
    Input,
    Radio,
    message,
    Modal,
    InputNumber,
} from 'antd';
import { useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { DeleteFilled, InfoCircleFilled } from '@ant-design/icons';
import {
    inviteMember,
    modifyInfo,
    queryMembers,
} from '&/api/personCenter/projectSpace';
import { getRoleList, queryMemberList } from '&/api/personCenter/teamManage';
import BaseModal from '&/components/BaseModal';
import { Select } from '&/components/AntdEncapsulation';
import usePermission from '&/utils/customHooks/usePermission';
import { formatPhone } from '&/utils/common.js';
import { logoQRcodeGenerate } from '&/utils/commonForPC';
import AuthSelect from '&/components/ProjectSpace/AuthSelect';
import ShareCard from '&/components/ShareCard';
import './SynergyTurbineTab.scss';

const { Item } = Form;

const SynergyTurbineTab = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState([]);
    const location = useLocation();
    const projectId = new URLSearchParams(location.search).get('id');
    const [permission, currentRole] = usePermission(projectId);

    useEffect(() => {
        queryList();
        queryRoleList();
    }, []);

    const queryList = () => {
        setIsLoading(true);
        queryMembers({
            projectId,
        })
            .then(({ data }) => {
                setResult(data ?? []);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const [roleList, setRoleList] = useState([]);

    const queryRoleList = () => {
        if (roleList.length) return;
        getRoleList({ projectId }).then(({ data }) => {
            const LIST = data.map(e => ({
                powerId: e.powerId ? JSON.parse(e.powerId) : [],
                label: e.roleName,
                value: e.roleId,
            }));
            setRoleList(LIST);
        });
    };

    const [selectType, setSelectType] = useState(0);

    function changeSelectType(e) {
        setSelectType(e.target.value);
    }

    const [selectRole, setSelectRole] = useState();
    const [members, setMembers] = useState([]); // 邀请的成员列表

    const onRoleChange = val => {
        setSelectRole(val);
    };

    const queryMemberDict = () => {
        queryMemberList().then(({ data }) => {
            const next = data.list
                .filter(e => {
                    return !result.some(item => item.userId === e.teamId);
                })
                .map(e => ({
                    value: e.teamId,
                    label: e.username,
                }));
            setMembers(next);
        });
    };

    const [inviteData, setInviteData] = useState({});
    const [limitNum, setLimitNum] = useState(1);

    useEffect(() => {
        selectRole === '6' && setLimitNum(1);
        selectType === 1 && getShareUrl();
    }, [selectRole]);

    const [loadUrl, setLoadUrl] = useState(false);
    // 获取邀请链接
    const getShareUrl = async () => {
        if (
            !limitNum ||
            (result.some(e => e.roleId === '6') && selectRole === '6')
        )
            return;

        setLoadUrl(true);
        try {
            const { data } = await inviteMember({
                role: selectRole,
                projectId,
                limit: limitNum,
            });
            const queryString = {
                ...data.url.invitation,
                invUserName: data.url.invUserName,
                type: data.url.type,
                ...data.url.project,
            };
            const url = `${
                data.url.domain.m
            }/pages/teamManage/InviteMembers?${new URLSearchParams(
                queryString
            ).toString()}`;

            const code = await logoQRcodeGenerate({ url });
            setInviteData({
                url: url,
                code,
                month: data.url.validDay?.month,
                day: data.url.validDay?.day,
                queryString,
            });
        } finally {
            setLoadUrl(false);
        }
    };

    const [didUpdate, forceUpdate] = useReducer(s => s + 1, 0);

    useEffect(() => {
        didUpdate > 0 && getShareUrl();
    }, [didUpdate]);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        modalVisible && [queryMemberDict, getShareUrl][selectType]();
    }, [modalVisible, selectType]);

    const onCloseAddModal = () => {
        setModalVisible(false);
        setSelectMember('');
    };

    function addTurbine() {
        setModalVisible(true);
    }

    const [selectMember, setSelectMember] = useState();
    const [addLoading, setAddLoading] = useState(false);

    async function onAdd() {
        if (selectType === 0) {
            if (!selectMember) return message.warning(intl.get('team.select'));
            try {
                setAddLoading(true);
                const { msg } = await modifyInfo({
                    dop: 1,
                    teamUserId: selectMember,
                    projectId,
                    role: selectRole,
                });
                message.success(msg);
                queryList();
            } finally {
                setAddLoading(false);
            }
        }
        onCloseAddModal();
    }

    const onSelecetMemberChange = val => {
        setSelectMember(val);
    };

    const SELECT_CONTENT = [
        <Item label={intl.get('projectSpace.selectMember')}>
            <Select
                value={selectMember}
                onChange={onSelecetMemberChange}
                options={members}
                placeholder={intl.get('projectSpace.selectMember')}
            />
        </Item>,
        <>
            <Item
                validateStatus={!limitNum ? 'error' : ' '}
                label={intl.get('team.num')}
                help={
                    limitNum
                        ? intl.get('team.inviteWay')
                        : intl.get('team.inputNum')
                }>
                <InputNumber
                    value={limitNum}
                    onChange={setLimitNum}
                    step={1}
                    min={1}
                    precision={0}
                    onBlur={forceUpdate}
                    max={selectRole === '6' ? 1 : 10}
                    placeholder={intl.get('team.inputNum')}
                />
            </Item>
            <ShareCard
                loadUrl={loadUrl}
                inviteData={inviteData}
                limitNum={limitNum}
            />
        </>,
    ];

    const onDelete = ({ userId }) => {
        const modal = Modal.confirm({
            content: intl.get('team.delWarn'),
            okText: intl.get('base.delete'),
            cancelText: intl.get('base.cancel'),
            okButtonProps: { loading: false },
            centered: true,
            onOk: async () => {
                modal.update({ okButtonProps: { loading: true } });
                try {
                    const { msg } = await modifyInfo({
                        projectId,
                        dop: 2,
                        memberId: userId,
                    });
                    message.success(msg);
                    queryList();
                } finally {
                    modal.update({ okButtonProps: { loading: false } });
                }
            },
        });
    };

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            render: (_, __, idx) => {
                return idx + 1;
            },
        },
        {
            title: intl.get('projectSpace.nickName'),
            dataIndex: 'username',
            ellipsis: true,
            render: role => formatPhone(role),
        },
        {
            title: intl.get('projectSpace.role'),
            dataIndex: 'roleName',
            render: (roleName, row) => {
                return row.powerRemark ? (
                    <Popover
                        overlayClassName="share-popover"
                        title={null}
                        content={row.powerRemark}
                        trigger="hover"
                        arrowPointAtCenter>
                        {roleName}
                    </Popover>
                ) : (
                    roleName
                );
            },
        },
        {
            title: intl.get('projectSpace.phone'),
            dataIndex: 'mobile',
            render: role => formatPhone(role),
        },
        {
            title: intl.get('base.remark'),
            dataIndex: 'remark',
        },
        {
            title: intl.get('projectSpace.email'),
            dataIndex: 'email',
        },
        {
            title: intl.get('projectSpace.lastLogin'),
            dataIndex: 'loginTime',
        },
        {
            title: intl.get('team.inviter'),
            dataIndex: 'inviteUserName',
            ellipsis: true,
        },
        {
            title: intl.get('base.operation'),
            align: 'right',
            className: 'last-col',
            key: '7',
            render: row => {
                return (
                    row.roleId !== '8' && (
                        <Space>
                            <i
                                style={{ fontSize: 14 }}
                                className={`${
                                    currentRole === 8
                                        ? 'filled icon-pencil'
                                        : 'filled icon-pencil type--disabled'
                                }`}
                                onClick={() =>
                                    currentRole === 8 ? openForm(row) : null
                                }></i>
                            <DeleteFilled
                                className={`${
                                    permission.includes('deleteMember')
                                        ? ''
                                        : 'type--disabled'
                                }`}
                                onClick={() =>
                                    permission.includes('deleteMember')
                                        ? onDelete(row)
                                        : undefined
                                }
                                style={{ fontSize: 14 }}
                            />
                        </Space>
                    )
                );
            },
        },
    ];

    const [visible, setVisible] = useState(false);
    const [activeRow, setActiveRow] = useState({});

    const openForm = row => {
        formRef.setFieldsValue({ remark: row.remark, role: row.roleId });
        setActiveRow({
            ...row,
            power: JSON.parse(row.power),
        });
        setVisible(true);
    };

    const onCancel = () => {
        setVisible(false);
    };

    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [formRef] = Form.useForm();

    const onFinish = values => {
        setIsSubmitLoading(true);
        const params = {
            projectId,
            dop: 3,
            role: values.role,
            power: activeRow.power,
            remark: values.remark,
            memberId: activeRow.userId,
        };
        modifyInfo(params)
            .then(({ msg }) => {
                message.success(msg);
                onCancel();
                queryList();
            })
            .finally(() => {
                setIsSubmitLoading(false);
            });
    };

    const onValuesChange = ({ role }) => {
        if (!role) return;
        const selected = roleList.find(item => item.value === role);
        selected &&
            setActiveRow(prev => ({
                ...prev,
                power: selected.powerId,
            }));
    };

    return (
        <div className="synergy-turbine">
            <Button
                className="top-right"
                type="primary"
                disabled={!permission.includes('addMember')}
                onClick={
                    permission.includes('addMember') ? addTurbine : undefined
                }>
                {intl.get('projectSpace.addMembers')}
            </Button>
            <header className="synergy-turbine-hint">
                <InfoCircleFilled className="hint-icon" />
                {intl.get('projectSpace.turbineHint')}
            </header>
            <Table
                className="normal-table"
                loading={isLoading}
                columns={columns}
                dataSource={result}
                size="small"
                pagination={false}
                rowKey="userId"
                rowClassName="custom-row"
            />

            <BaseModal
                width="36.25rem"
                visible={modalVisible}
                getContainer={false}
                footer={
                    <Space>
                        <Button onClick={onCloseAddModal}>
                            {intl.get('base.cancel')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={onAdd}
                            disabled={
                                !selectRole ||
                                (result.some(e => e.roleId === '6') &&
                                    selectRole === '6')
                            }
                            loading={addLoading}>
                            {intl.get('base.confirm')}
                        </Button>
                    </Space>
                }
                title={intl.get('projectSpace.addMembers')}
                onCancel={onCloseAddModal}>
                <Form
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    className="add-form">
                    <section className="form-group">
                        <Item
                            validateStatus={
                                result.some(e => e.roleId === '6') &&
                                selectRole === '6'
                                    ? 'error'
                                    : ''
                            }
                            label={intl.get('projectSpace.selectRole')}>
                            <Select
                                value={selectRole}
                                options={roleList}
                                placeholder={intl.get('team.roleSelectWarn')}
                                onChange={onRoleChange}
                            />
                            <span
                                className={`${
                                    result.some(e => e.roleId === '6') &&
                                    selectRole === '6'
                                        ? 'warn-msg--danger warn-msg'
                                        : 'warn-msg'
                                }`}>
                                <i className="icon-info-circle2 filled"></i>{' '}
                                {intl.get('team.onlyOne')}
                            </span>
                        </Item>
                        {selectRole &&
                            !(
                                result.some(e => e.roleId === '6') &&
                                selectRole === '6'
                            ) && (
                                <>
                                    <Item wrapperCol={{ offset: 4 }}>
                                        <Radio.Group
                                            value={selectType}
                                            onChange={changeSelectType}>
                                            <Radio value={0}>
                                                {intl.get(
                                                    'projectSpace.listInvite'
                                                )}
                                            </Radio>
                                            <Radio value={1}>
                                                {intl.get(
                                                    'projectSpace.inviteMembers'
                                                )}
                                            </Radio>
                                        </Radio.Group>
                                    </Item>
                                    {SELECT_CONTENT[selectType]}
                                </>
                            )}
                    </section>
                </Form>
            </BaseModal>

            <BaseModal
                width="62rem"
                visible={visible}
                getContainer={false}
                footer={
                    <Space>
                        <Button onClick={onCancel}>
                            {intl.get('base.cancel')}
                        </Button>
                        <Button
                            type="primary"
                            loading={isSubmitLoading}
                            onClick={formRef.submit}>
                            {intl.get('base.confirm')}
                        </Button>
                    </Space>
                }
                title={intl.get('base.edit')}
                onCancel={onCancel}>
                <Form
                    form={formRef}
                    inline
                    labelCol={{ span: 4 }}
                    labelAlign="left"
                    onValuesChange={onValuesChange}
                    onFinish={onFinish}>
                    <section className="form-group">
                        <div className="inline-form">
                            <Item label={intl.get('projectSpace.nickName')}>
                                {activeRow.username}
                            </Item>
                            <Item
                                label={intl.get('projectSpace.role')}
                                name="role"
                                rules={[
                                    {
                                        validator: async (rule, val) => {
                                            if (val !== '6')
                                                return Promise.resolve();
                                            const proprietor = result.find(
                                                e => e.roleId === '6'
                                            );
                                            if (
                                                proprietor &&
                                                proprietor.userId !==
                                                    activeRow.userId
                                            ) {
                                                throw new Error(
                                                    intl.get('team.onlyOne')
                                                );
                                            } else {
                                                return Promise.resolve();
                                            }
                                        },
                                    },
                                ]}>
                                <Select
                                    options={roleList}
                                    style={{ width: '14rem' }}
                                />
                            </Item>
                            <Item name="remark" label={intl.get('base.remark')}>
                                <Input
                                    placeholder={intl.get('team.remarkWarn')}
                                    style={{ width: '14rem' }}
                                />
                            </Item>
                        </div>
                        <AuthSelect
                            treeValue={activeRow.power}
                            onAuthChange={power =>
                                setActiveRow(prev => ({
                                    ...prev,
                                    power,
                                }))
                            }></AuthSelect>
                    </section>
                </Form>
            </BaseModal>
        </div>
    );
};

export default SynergyTurbineTab;
